import { MealPlanDetail } from './MealPlan'
import { PaymentStatus, SubscriptionStatus } from './Order'
import { ProductSnapshot } from './Product'

export enum PetWeightChangeDesire {
  GAIN = 'GAIN',
  LOSE = 'LOSE',
  STAY = 'STAY',
}

export enum PetKinds {
  DOG = 'DOG',
  CAT = 'CAT',
}

export enum PetGenders {
  MALE = 'MALE',
  FEMALE = 'FEMALE',
}

export enum PetSizes {
  SMALL = 'SMALL',
  MEDIUM = 'MEDIUM',
  LARGE = 'LARGE',
}

export enum BodyConditions {
  UNDERWEIGHT = 'UNDERWEIGHT',
  SKINNY = 'SKINNY',
  IDEAL = 'IDEAL',
  CHUBBY = 'CHUBBY',
  OVERWEIGHT = 'OVERWEIGHT',
}

export enum ActivityLevels {
  INACTIVE = 'INACTIVE',
  MODERATELY_ACTIVE = 'MODERATELY_ACTIVE',
  ACTIVE = 'ACTIVE',
  VERY_ACTIVE = 'VERY_ACTIVE',
}

export enum Proteins {
  BEEF = 'BEEF',
  CHICKEN = 'CHICKEN',
  TURKEY = 'TURKEY',
  DUCK = 'DUCK',
  LAMB = 'LAMB',
  VENISON = 'VENISON',
}

export enum FreezeDriedProteins {
  BEEF = 'BEEF',
  CHICKEN = 'CHICKEN',
}

export enum EatingHabits {
  PICKY_EATER = 'PICKY_EATER',
  GOOD_EATER = 'GOOD_EATER',
  WILL_EAT_ANYTHING = 'WILL_EAT_ANYTHING',
}

export enum HealthGoals {
  REDUCTION_IN_ALLERGIES = 'REDUCTION_IN_ALLERGIES',
  WEIGHT_MANAGEMENT = 'WEIGHT_MANAGEMENT',
  INCREASED_LONGEVITY = 'INCREASED_LONGEVITY',
  OVERALL_HEALTH = 'OVERALL_HEALTH',
  DIGESTION_IMPROVEMENT = 'DIGESTION_IMPROVEMENT',
  HEALTHIER_COAT = 'HEALTHIER_COAT',
  MEALTIME_EXCITEMENT = 'MEALTIME_EXCITEMENT',
}

export enum HealthGoalsRanked {
  WEIGHT_MANAGEMENT = 'WEIGHT_MANAGEMENT',
  INCREASED_LONGEVITY = 'INCREASED_LONGEVITY',
  DIGESTION_IMPROVEMENT = 'DIGESTION_IMPROVEMENT',
  HEALTHIER_COAT = 'HEALTHIER_COAT',
  MEALTIME_EXCITEMENT = 'MEALTIME_EXCITEMENT',
}

export enum HealthIssues {
  GUT_DIGESTION = 'GUT_DIGESTION',
  ALLERGIES = 'ALLERGIES',
  MOBILITY = 'MOBILITY',
  ORAL_HEALTH = 'ORAL_HEALTH',
  BEHAVIORAL = 'BEHAVIORAL',
  EYE_HEALTH = 'EYE_HEALTH',
  SKIN_AND_COAT = 'SKIN_AND_COAT',
  CANCER = 'CANCER',
  DIABETES = 'DIABETES',
  NONE = 'NONE',
}

export enum PetAdultSizes {
  XSMALL = 'XS',
  SMALL = 'S',
  MEDIUM = 'M',
  LARGE = 'L',
  XLARGE = 'XL',
}

export enum PetAdultSizeWeightClasses {
  XS = 'Under 15lbs',
  S = '15-30lbs',
  M = '30-60lbs',
  L = '60-100lbs',
  XL = '100lbs +',
}

export type Pet = {
  petId: string
  name: string
  kind: PetKinds
}

export type PetDetail = {
  petId: string
  ownerId: string
  subscriptionId: string
  name: string
  kind: PetKinds
  birthDate: string
  gender: PetGenders
  isSpayed: boolean
  breed: string
  weight: number
  bodyCondition: BodyConditions
  activityLevel: ActivityLevels
  isAlreadyOnRawDiet: boolean
  dietPrior?: DietPrior
  allergies: Proteins[]
  updatedAt: string
  createdAt: string
  brightbackUrl?: any
  products?: ProductSnapshot[]
  mealPlan: MealPlanDetail

  isPriceUpToDate: boolean
  subscription: {
    clientFullName: string
    clientId: string
    externalId: string
    frequency: number
    lastOrderCreationDate: string
    nextOrderCreationDate: string
    notes: string
    status: SubscriptionStatus
    subscriptionId: string
    type: 'MEAL_PLAN' | 'BULK_PRODUCTS'
    tags: SubscriptionTag[]
    paymentDetails: {
      priceBreakdown: {
        discount: number
        shipping: number
        subtotal: number
        taxes: number
        total: number
      }
      paymentDate: string
      status: PaymentStatus
    }
    orderCount: number
  }
  weightChangeDesire: PetWeightChangeDesire
  size?: PetSizes
  isPuppy?: boolean
  adultWeight?: number
  adultSize?: PetAdultSizes
  healthGoals?: HealthGoals[]
  eatingHabits?: EatingHabits
}

export enum SubscriptionTag {
  CANCELED_OTHER = 'CANCELED_OTHER',
  CANCELED_TOO_EXPENSIVE = 'CANCELED_TOO_EXPENSIVE',
  CANCELED_PET_DIDN_T_LIKE_IT = 'CANCELED_PET_DIDN_T_LIKE_IT',
  CANCELED_PET_LOSS = 'CANCELED_PET_LOSS',
  CANCELED_DIGESTIVE_ISSUE = 'CANCELED_DIGESTIVE_ISSUE',
  CANCELED_ALLERGIES = 'CANCELED_ALLERGIES',
  PREFERED_UPS = 'PREFERED_UPS',
  PREFERED_FEDEX = 'PREFERED_FEDEX',
  IMPORTED_FROM_OLD_SYSTEM = 'IMPORTED_FROM_OLD_SYSTEM',
  PUPPIES_MEAL_PLAN = 'PUPPIES_MEAL_PLAN',
  OUTREACH_TEST = 'OUTREACH_TEST',
  SAVE_A_SALE = 'SAVE_A_SALE',
  SAVER_BOX = 'SAVER_BOX',
  FLEX_BOX = 'FLEX_BOX',
}

export type EditPetDetail = {
  name: string
  kind: PetKinds
  birthDate: string
  isSpayed: boolean
  breed: string
  weight: number
  bodyCondition: BodyConditions
  activityLevel: ActivityLevels
  allergies: Proteins[]
  isAlreadyOnRawDiet: boolean
  weightChangeDesire: PetWeightChangeDesire
  size?: PetSizes
  isPuppy?: boolean
  adultWeight?: number
  healthGoals?: HealthGoals[]
}

export type BirthDate = {
  month?: number
  day?: number
  year?: number
}

export enum DietPrior {
  FRESH_RAW_FOOD = 'FRESH_RAW_FOOD',
  FREEZE_DRIED_RAW_FOOD = 'FREEZE_DRIED_RAW_FOOD',
  COOKED_FOOD = 'COOKED_FOOD',
  WET_FOOD = 'WET_FOOD',
  AIR_DRIED_FOOD = 'AIR_DRIED_FOOD',
  KIBBLE = 'KIBBLE',
  OTHER = 'OTHER',
}
